import type { EvidenceRecord, PermissionInput } from "../types";

export type CommunicationChannel = "sendgrid_email" | "twilio_sms";

export type CarrierFollowUpAction =
  | "request_eta"
  | "request_tracking"
  | "request_pickup_confirmation"
  | "request_delivery_confirmation"
  | "request_pod";

export type CommunicationAction = CarrierFollowUpAction | "send_customer_update";

export interface FollowUpMessageInput {
  tenantId: string;
  loadId: string;
  loadReference: string;
  actionType: CommunicationAction;
  channel: CommunicationChannel;
  carrierName: string;
  contact: string;
}

export interface OutboundMessageDraft {
  tenantId: string;
  loadId: string;
  channel: CommunicationChannel;
  audience: PermissionInput["audience"];
  to: string;
  subject?: string;
  body: string;
  templateId: string;
  requiresApproval: boolean;
  payload: Record<string, unknown>;
}

export interface InboundCarrierReply {
  tenantId: string;
  loadId: string;
  channel: CommunicationChannel;
  from: string;
  body: string;
  receivedAt: string;
  rawPayload: Record<string, unknown>;
}

export function buildCarrierFollowUpMessage(input: FollowUpMessageInput): OutboundMessageDraft {
  const template = templates[input.actionType];
  const body = template.body(input.loadReference, input.carrierName);
  const audience = input.actionType === "send_customer_update" ? "customer" : "carrier";
  // Epic B: customer always approval; carrier templates draft-and-hold until
  // tenant policy graduates send_carrier_message (protected by command guards).
  const requiresApproval = audience === "customer" || input.channel === "sendgrid_email";

  return {
    tenantId: input.tenantId,
    loadId: input.loadId,
    channel: input.channel,
    audience,
    to: input.contact,
    subject: input.channel === "sendgrid_email" ? template.subject(input.loadReference) : undefined,
    body,
    templateId: template.id,
    requiresApproval,
    payload: {
      actionType: input.actionType,
      carrierName: input.carrierName,
      loadReference: input.loadReference,
    },
  };
}

export function inboundCarrierReplyToEvidence(input: InboundCarrierReply): EvidenceRecord {
  const source = input.channel === "twilio_sms" ? "twilio" : "sendgrid";

  return {
    id: `reply_${input.loadId}_${Date.parse(input.receivedAt)}`,
    tenantId: input.tenantId,
    loadId: input.loadId,
    type: "carrier_reply",
    source,
    occurredAt: input.receivedAt,
    receivedAt: input.receivedAt,
    reliabilityScore: input.channel === "twilio_sms" ? 0.7 : 0.72,
    payload: {
      from: input.from,
      body: input.body,
      intent: inferReplyIntent(input.body),
      raw: input.rawPayload,
    },
  };
}

const templates: Record<
  CommunicationAction,
  {
    id: string;
    subject: (loadReference: string) => string;
    body: (loadReference: string, carrierName: string) => string;
  }
> = {
  request_eta: {
    id: "carrier_eta_request_v1",
    subject: (loadReference) => `ETA request for ${loadReference}`,
    body: (loadReference, carrierName) =>
      `${carrierName}, please send current ETA and location for ${loadReference}.`,
  },
  request_tracking: {
    id: "carrier_tracking_request_v1",
    subject: (loadReference) => `Tracking request for ${loadReference}`,
    body: (loadReference, carrierName) =>
      `${carrierName}, please confirm tracking is active for ${loadReference}.`,
  },
  request_pickup_confirmation: {
    id: "carrier_pickup_confirmation_v1",
    subject: (loadReference) => `Pickup confirmation for ${loadReference}`,
    body: (loadReference, carrierName) =>
      `${carrierName}, please confirm driver status for pickup on ${loadReference}.`,
  },
  request_delivery_confirmation: {
    id: "carrier_delivery_confirmation_v1",
    subject: (loadReference) => `Delivery confirmation for ${loadReference}`,
    body: (loadReference, carrierName) =>
      `${carrierName}, please confirm delivery status for ${loadReference}.`,
  },
  request_pod: {
    id: "carrier_pod_request_v1",
    subject: (loadReference) => `POD request for ${loadReference}`,
    body: (loadReference, carrierName) =>
      `${carrierName}, please send the signed POD for ${loadReference}.`,
  },
  send_customer_update: {
    id: "customer_update_draft_v1",
    subject: (loadReference) => `Update for ${loadReference}`,
    body: (loadReference) =>
      `Draft customer update for ${loadReference}. Human approval is required before sending.`,
  },
};

function inferReplyIntent(body: string): string {
  const normalized = body.toLowerCase();

  if (normalized.includes("pod") || normalized.includes("proof of delivery")) return "pod_available";
  if (normalized.includes("delivered") || normalized.includes("empty")) return "delivery_confirmed";
  if (normalized.includes("loaded") || normalized.includes("picked up") || normalized.includes("on site")) {
    return "pickup_confirmed";
  }
  if (normalized.includes("eta")) return "eta_update";
  if (normalized.includes("tracking")) return "tracking_update";

  return "general_reply";
}
